import React, { Dispatch, SetStateAction, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  Grid,
  TextField,
  DialogActions,
  Button,
  DialogTitle,
  FormLabel,
  FormControl,
  FormControlLabel,
  Checkbox,
  InputLabel,
  Select,
  MenuItem,
  Tab,
  Tabs,
} from "@mui/material";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { AccountBalance, BusinessCenter, ScreenSearchDesktop } from "@mui/icons-material";
import axios from "axios";
import PermissionsCheckboxGroup, { Permission } from "./PermissionsCheckboxGroup";
import useDialog from "../../hooks/useDialog";
import { getUserTabFromRoles, isTabDisabled } from "../../utils/userFormatting";

const DEPARTMENTS = {
  RISK: "რისკების დეპარტამენტი",
  SME: "მცირე და საშუალო ბიზნესის განვითარების დეპარტამენტი",
  ADMIN: "ადმინისტრაციული დეპარტამენტი",
};

type agencyPosition = {
  label: string;
  value: number;
  isVisibleTo?: string[];
};

type bank = {
  _id: string;
  name: string;
};

type agencyDepartment = {
  _id: string;
  title: string;
};

type UserForm = {
  firstName: string;
  lastName: string;
  email: string;
  bank?: string; // This will be the ID of the bank
  permissions?: string[]; // This will be an array of permission values
  agency_level?: number | string;
  agency_department?: string;
};

type UserCreateDialogProps = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  banks: bank[];
  agencyDepartments: agencyDepartment[];
  isBank: boolean;
  isMonitoring: boolean;
  isMonitoringCGS: boolean;
  isMonitoringReview: boolean;
  editUser?: any; // User object to edit (if provided, dialog is in edit mode)
  onUserUpdated?: () => void; // Callback after user is updated
};

export default function UserCreateDialog({
  open,
  setOpen,
  banks = [],
  agencyDepartments = [],
  isBank,
  isMonitoring,
  isMonitoringCGS,
  isMonitoringReview,
  editUser,
  onUserUpdated,
}: UserCreateDialogProps) {
  const initialUser = {
    firstName: "",
    lastName: "",
    email: "",
    bank: "",
    permissions: [],
    agency_level: 0,
    agency_department: "",
  };

  const [user, setUser] = React.useState<UserForm>(initialUser);
  const isEditMode = !!editUser;

  const agencyPositions: agencyPosition[] = [
    { label: "სოციალური რისკების სპეციალისტი", value: -2, isVisibleTo: [DEPARTMENTS.RISK, DEPARTMENTS.SME] },
    { label: "გარემოსდაცვითი სპეციალისტი", value: -1, isVisibleTo: [DEPARTMENTS.RISK, DEPARTMENTS.SME] },
    { label: "მონიტორინგი", value: 0, isVisibleTo: [DEPARTMENTS.RISK, DEPARTMENTS.SME] },
    { label: "დეპ. თანამშრომელი", value: 1, isVisibleTo: [DEPARTMENTS.RISK, DEPARTMENTS.SME, DEPARTMENTS.ADMIN] },
    { label: "დეპ. უფროსი", value: 2, isVisibleTo: [DEPARTMENTS.RISK, DEPARTMENTS.SME] },
    { label: "სააგენტოს დირექტორი", value: 3, isVisibleTo: [DEPARTMENTS.RISK, DEPARTMENTS.SME] },
  ];

  const [loading, setLoading] = React.useState(false);
  const [tab, setTab] = React.useState("bank");
  const { showSnackbar } = useDialog();

  // Prefill form when editing a user
  useEffect(() => {
    if (isEditMode && editUser && open) {
      const nameParts = editUser.name?.split(" ") || ["", ""];
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Determine the tab based on user's role
      const userTab = getUserTabFromRoles(editUser.roles, !!editUser.bank);
      setTab(userTab);
      setUser({
        firstName,
        lastName,
        email: editUser.email || "",
        bank: editUser.bank?._id || editUser.bank || "",
        permissions: editUser.permissions || [],
        agency_level: editUser.agencyLevel ?? 0,
        agency_department: editUser.agencyDepartment?._id || editUser.agencyDepartment || "",
      });
    } else if (!isEditMode) {
      setUser(initialUser);
    }
  }, [isEditMode, editUser, open]);

  useEffect(() => {
    if (!isEditMode) {
      setUser(initialUser);
    }
  }, [tab]);

  useEffect(() => {
    if (!isEditMode) {
      if (isBank) setTab("bank");
      if (isMonitoring) setTab("monitoring");
      if (isMonitoringCGS) setTab("monitoring_cgs");
      if (isMonitoringReview) setTab("monitoring_review");
    }
  }, [isBank, isMonitoring, isMonitoringCGS, isMonitoringReview, isEditMode]);

  useEffect(() => {
    if (!user.agency_department) return;

    const selectedDepartment = agencyDepartments.find((d) => d._id === user.agency_department);
    if (!selectedDepartment) return;

    const currentLevel = user.agency_level;
    const isVisible = agencyPositions.some((p) => p.value === currentLevel && p.isVisibleTo?.includes(selectedDepartment.title));

    if (!isVisible && currentLevel !== "") {
      setUser((prev) => ({ ...prev, agency_level: "" }));
    }
  }, [user.agency_department]);

  const permissions: Permission[] = [
    { name: "ბანკჰედი", value: "ADD_USER", implies: ["SUBMIT_BUSINESS_APPLICATIONS", "SUBMIT_MORTGAGE_APPLICATIONS"] },
    { name: "ინდუსტრიული და უნივერსალური აპლიკაციების დამატება", value: "SUBMIT_BUSINESS_APPLICATIONS" },
    { name: "იპოთეკური აპლიკაციების დამატება", value: "SUBMIT_MORTGAGE_APPLICATIONS" },
  ];

  const monitoringPermissions: Permission[] = [
    { name: "მონიტორინგის მთავარი მომხმარებელი", value: "ADD_USER", implies: ["SUBMIT_MONITORING_FORMS"] },
    { name: "მონიტორინგის სპეციალისტი", value: "SUBMIT_MONITORING_FORMS" },
  ];

  const monitoringReviewPermissions: Permission[] = [
    { name: "მომხმარებლების დამატება", value: "ADD_USER" },
    { name: "მონიტორინგი", value: "REVIEW_MONITORING", disabled: true },
    { name: "მიკროს მონიტორინგი (CGS)", value: "REVIEW_MONITORING_CGS" },
  ];

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setUser({ ...user, [event.target.name]: event.target.value });
  }

  function createUser() {
    setLoading(true);
    axios
      .post("/auth/signup", {
        name: user.firstName + " " + user.lastName,
        email: user.email,
        bank: user.bank,
        role: tab,
        permissions: user.permissions,
        agency_level: user.agency_level,
        agency_department: user.agency_department,
      })
      .then((res) => {
        showSnackbar(res.data.message, "success");
        setLoading(false);
        if (onUserUpdated) onUserUpdated();
      })
      .catch((err) => {
        showSnackbar(err.response.data.message || "შეცდომა", "error");
        console.error(err.response.data);
        setLoading(false);
      });
  }

  function updateUser() {
    setLoading(true);
    axios
      .put(`/auth/user/${editUser._id}`, {
        name: user.firstName + " " + user.lastName,
        email: user.email,
        bank: user.bank,
        role: tab,
        permissions: user.permissions,
        agency_level: user.agency_level,
        agency_department: user.agency_department,
      })
      .then((res) => {
        showSnackbar(res.data.message, "success");
        setLoading(false);
        if (onUserUpdated) onUserUpdated();
      })
      .catch((err) => {
        showSnackbar(err.response?.data?.message || "შეცდომა", "error");
        console.error(err.response?.data);
        setLoading(false);
      });
  }

  function handleSubmit() {
    if (isEditMode) {
      updateUser();
    } else {
      createUser();
    }
  }

  function handleDeactivation() {
    const deactivate = !editUser.deactivated;
    axios
      .post("/auth/deactivation", { userId: editUser._id, deactivate })
      .then((res) => {
        showSnackbar(res.data.message, "success");
        if (onUserUpdated) onUserUpdated();
        setOpen(false);
      })
      .catch((err) => {
        showSnackbar(err.response?.data?.message || "შეცდომა", "error");
      });
  }

  return (
    <Dialog open={open} onClose={() => setOpen(false)}>
      <TabContext value={tab}>
        <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)} variant="fullWidth">
          <Tab 
            icon={<BusinessCenter />} 
            disabled={isTabDisabled("bank", tab, isEditMode, { isBank, isMonitoring, isMonitoringCGS, isMonitoringReview })} 
            label="ბანკი" 
            value="bank" 
          />
          <Tab 
            icon={<AccountBalance />} 
            disabled={isTabDisabled("agency", tab, isEditMode, { isBank, isMonitoring, isMonitoringCGS, isMonitoringReview })} 
            label="სააგენტო" 
            value="agency" 
          />
          <Tab 
            icon={<ScreenSearchDesktop />} 
            disabled={isTabDisabled("monitoring", tab, isEditMode, { isBank, isMonitoring, isMonitoringCGS, isMonitoringReview })} 
            label="მონიტორინგი" 
            value="monitoring" 
          />
          <Tab 
            icon={<ScreenSearchDesktop />} 
            disabled={isTabDisabled("monitoring_cgs", tab, isEditMode, { isBank, isMonitoring, isMonitoringCGS, isMonitoringReview })} 
            label="მიკროს მონიტორინგი" 
            value="monitoring_cgs" 
          />
          <Tab 
            icon={<ScreenSearchDesktop />} 
            disabled={isTabDisabled("monitoring_review", tab, isEditMode, { isBank, isMonitoring, isMonitoringCGS, isMonitoringReview })} 
            label="მონიტორინგის განმხილველი" 
            value="monitoring_review" 
          />
        </Tabs>
        <DialogTitle>{isEditMode ? "მომხმარებლის რედაქტირება" : "მომხმარებლის შექმნა"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <TextField fullWidth name="firstName" value={user.firstName} onChange={handleChange} placeholder="სახელი" label="სახელი" />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth name="lastName" value={user.lastName} onChange={handleChange} placeholder="გვარი" label="გვარი" />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth name="email" value={user.email} onChange={handleChange} placeholder="ელ-ფოსტა" label="ელ-ფოსტა" />
            </Grid>
            <Grid item xs={12} hidden={tab !== "bank"}>
              <TabPanel value="bank" sx={{ width: "100%", padding: 0 }}>
                <Grid item xs={12} hidden={isBank}>
                  {/* User will select bank name, value will be registered as ID */}
                  <InputLabel>ბანკი</InputLabel>
                  <Select fullWidth value={user.bank} onChange={(event) => setUser({ ...user, bank: event.target.value as string })}>
                    {banks.map((bank) => (
                      <MenuItem key={bank._id} value={bank._id}>
                        {bank.name}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <PermissionsCheckboxGroup
                    permissions={permissions}
                    userPermissions={user.permissions || []}
                    setUserPermissions={(newPermissions) => setUser({ ...user, permissions: newPermissions })}
                  />
                </Grid>
              </TabPanel>
            </Grid>
            <Grid item xs={12}>
              <TabPanel value="agency" sx={{ width: "100%", padding: 0 }}>
                <Grid item xs={12}>
                  <InputLabel>პოზიცია</InputLabel>
                  <Select fullWidth value={user.agency_level} onChange={(event) => setUser({ ...user, agency_level: event.target.value as number })}>
                    {agencyPositions.map((position) => {
                      const selectedDep = agencyDepartments.find((d) => d._id === user.agency_department);
                      const isVisible = !selectedDep || !position.isVisibleTo || position.isVisibleTo.includes(selectedDep.title);
                      return (
                        <MenuItem key={position.value} value={position.value} disabled={!isVisible}>
                          {position.label}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <InputLabel>დეპარტამენტი</InputLabel>
                  <Select fullWidth value={user.agency_department} onChange={(event) => setUser({ ...user, agency_department: event.target.value as string })}>
                    {agencyDepartments.map((department) => (
                      <MenuItem key={department._id} value={department._id}>
                        {department.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
              </TabPanel>
            </Grid>
            {/* Monitoring tab */}
            <Grid item xs={12}>
              <TabPanel value="monitoring" sx={{ width: "100%", padding: 0 }}>
                <Grid item xs={12}>
                  <PermissionsCheckboxGroup
                    permissions={monitoringPermissions}
                    userPermissions={user.permissions || []}
                    setUserPermissions={(newPermissions) => setUser({ ...user, permissions: newPermissions })}
                  />
                </Grid>
              </TabPanel>
              <TabPanel value="monitoring_cgs" sx={{ width: "100%", padding: 0 }}>
                <Grid item xs={12}>
                  <PermissionsCheckboxGroup
                    permissions={monitoringPermissions}
                    userPermissions={user.permissions || []}
                    setUserPermissions={(newPermissions) => setUser({ ...user, permissions: newPermissions })}
                  />
                </Grid>
              </TabPanel>
              <TabPanel value="monitoring_review" sx={{ width: "100%", padding: 0 }}>
                <Grid item xs={12}>
                  <PermissionsCheckboxGroup
                    permissions={monitoringReviewPermissions}
                    userPermissions={user.permissions || []}
                    setUserPermissions={(newPermissions) => setUser({ ...user, permissions: newPermissions })}
                  />
                </Grid>
              </TabPanel>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            უკან
          </Button>
          {isEditMode && (
            <Button onClick={handleDeactivation} color={editUser?.deactivated ? "primary" : "secondary"} variant="outlined" style={{ marginRight: "auto" }}>
              {editUser?.deactivated ? "მომხმარებლის აქტივაცია" : "მომხმარებლის დეაქტივაცია"}
            </Button>
          )}
          <Button
            onClick={() => {
              setOpen(false);
              handleSubmit();
            }}
            variant="contained"
            color="primary"
          >
            {isEditMode ? "შენახვა" : "მომხმარებლის დამატება"}
          </Button>
        </DialogActions>
      </TabContext>
    </Dialog>
  );
}
